import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import type { ReactNode } from "react"

/**
 * Envoltorio de lo que aparece y desaparece por una acción: un formulario que se
 * abre, un panel de detalle, una propuesta.
 *
 * ⚠ **Sólo para lo que abre una persona.** Nada que se refresque solo pasa por aquí:
 * el sondeo de 60 s haría que el tablero entero entrara y saliera cada minuto
 * (regla 11). Aparecer es la respuesta a un clic, no a que llegaran datos.
 *
 * ⚠ **La salida es más rápida que la entrada** —120 ms contra 180—, y no por
 * simetría rota: lo que se va ya no interesa, y hacer esperar por ello es lo que
 * convierte una interfaz animada en una interfaz lenta.
 *
 * ⚠ **Se anima la altura además de la opacidad.** Es la excepción a «sólo
 * `transform` y `opacity`»: sin ella, el contenido de debajo salta de golpe cuando
 * el panel aparece, y ese salto se nota mucho más que el coste de recalcular una
 * caja. Motion la mide una vez, no en cada cuadro.
 */
export function Aparece({
  children,
  visible,
  className,
}: {
  children: ReactNode
  visible: boolean
  className?: string
}) {
  const sinMovimiento = useReducedMotion()

  return (
    <AnimatePresence initial={false}>
      {visible && (
        <motion.div
          className={className}
          // ⚠ **Sin `height` (12/10/2026).** Desplegaba de 0 a `auto`, que recalcula la
          // maquetación en cada fotograma: la regla es sólo `transform` y `opacity`. El
          // hueco aparece de una vez y lo que se anima es el contenido, que baja 4 px.
          initial={sinMovimiento ? false : { opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={sinMovimiento ? { opacity: 0 } : { opacity: 0, y: -4 }}
          transition={{
            duration: sinMovimiento ? 0 : 0.18,
            ease: [0.16, 1, 0.3, 1],
            opacity: { duration: sinMovimiento ? 0 : 0.12 },
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
